/**
 * pdf.js PDFViewerApplication.cleanup() calls pdfDocument.cleanup() without awaiting.
 * If a page is still rendering, WorkerTransport.startCleanup rejects with
 * "startCleanup: Page N is currently rendering" as an unhandled promise.
 *
 * Known ngx-extended-pdf-viewer / pdf.js nuisance (not an app logic bug):
 * https://github.com/stephanrauh/ngx-extended-pdf-viewer/issues/645
 *
 * Swallowing that specific rejection is safe — cleanup finishes after the render ends.
 */

type PdfDocLike = {
  cleanup?: (...args: unknown[]) => Promise<unknown> | unknown;
  __cleanupPatched?: boolean;
  numPages?: number;
};

type PdfViewerAppLike = {
  cleanup?: (...args: unknown[]) => unknown;
  __appCleanupPatched?: boolean;
  pdfDocument?: PdfDocLike | null;
  pdfViewer?: any;
  eventBus?: { dispatch?: (name: string, data?: unknown) => void };
  pagesCount?: number;
};

export type PdfViewerApplicationSources = {
  loaderApp?: any;
  notificationApp?: any;
};

/**
 * ngx v29 loads pdf.js as an ES module (`isViewerEmbedded = true`) and keeps
 * `PDFViewerApplication` on PDFScriptLoaderService — not on `window`.
 */
export function resolvePdfViewerApplication(sources?: PdfViewerApplicationSources): PdfViewerAppLike | null {
  const fromWindow = (window as { PDFViewerApplication?: PdfViewerAppLike }).PDFViewerApplication;
  if (fromWindow?.eventBus) {
    return fromWindow;
  }
  const fromLoader = sources?.loaderApp;
  if (fromLoader?.eventBus) {
    mirrorPdfViewerApplicationOnWindow(fromLoader);
    return fromLoader;
  }
  const fromNotification = sources?.notificationApp;
  if (fromNotification?.eventBus) {
    mirrorPdfViewerApplicationOnWindow(fromNotification);
    return fromNotification;
  }
  return fromLoader ?? fromNotification ?? fromWindow ?? null;
}

/** Legacy call sites (OCR, detection, document-viewer) still read `window.PDFViewerApplication`. */
function mirrorPdfViewerApplicationOnWindow(app: PdfViewerAppLike): void {
  if (typeof window === 'undefined') return;
  const w = window as { PDFViewerApplication?: PdfViewerAppLike };
  if (!w.PDFViewerApplication?.eventBus) {
    w.PDFViewerApplication = app;
  }
}

export function resolvePdfDocument(app?: PdfViewerAppLike | null): PdfDocLike | null {
  const pdfApp = app ?? resolvePdfViewerApplication();
  if (!pdfApp) return null;
  return (pdfApp.pdfDocument as PdfDocLike | undefined) ?? (pdfApp.pdfViewer?.pdfDocument as PdfDocLike | undefined) ?? null;
}

export function isPdfJsCurrentlyRenderingCleanupError(message: string): boolean {
  return (
    /startCleanup:\s*Page\s+\d+\s+is currently rendering/i.test(message) ||
    (/currently rendering/i.test(message) && /startCleanup/i.test(message))
  );
}

function swallowCleanupRejection(err: unknown): void {
  const message = String((err as { message?: unknown })?.message ?? err ?? '');
  if (isPdfJsCurrentlyRenderingCleanupError(message) || /currently rendering/i.test(message)) {
    return;
  }
  throw err;
}

export function patchPdfDocumentCleanup(pdfDoc: PdfDocLike | null | undefined): void {
  const originalCleanup = pdfDoc?.cleanup?.bind(pdfDoc);
  if (!originalCleanup || !pdfDoc || pdfDoc.__cleanupPatched) {
    return;
  }

  pdfDoc.__cleanupPatched = true;
  pdfDoc.cleanup = async function (keepLoadedFonts = false) {
    try {
      await Promise.resolve(originalCleanup(keepLoadedFonts));
    } catch (err) {
      swallowCleanupRejection(err);
    }
  };
}

/**
 * Wrap PDFViewerApplication.cleanup so pdfDocument.cleanup is patched before the
 * fire-and-forget call inside pdf.js — that is what prevents unhandled rejections.
 */
export function patchPdfViewerApplicationCleanup(app?: PdfViewerAppLike | null): void {
  const pdfApp =
    app ??
    ((window as { PDFViewerApplication?: PdfViewerAppLike }).PDFViewerApplication ?? null);
  if (!pdfApp || pdfApp.__appCleanupPatched) {
    return;
  }

  const originalCleanup = pdfApp.cleanup?.bind(pdfApp);
  if (!originalCleanup) {
    return;
  }

  pdfApp.__appCleanupPatched = true;
  pdfApp.cleanup = function (...args: unknown[]) {
    patchPdfDocumentCleanup(pdfApp.pdfDocument ?? null);
    try {
      const result = originalCleanup(...args);
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        return (result as Promise<unknown>).catch(swallowCleanupRejection);
      }
      return result;
    } catch (err) {
      swallowCleanupRejection(err);
    }
  };
}

export function patchWindowPdfViewerCleanup(sources?: PdfViewerApplicationSources): void {
  const pdfApp = resolvePdfViewerApplication(sources);
  patchPdfDocumentCleanup(pdfApp?.pdfDocument ?? null);
  patchPdfViewerApplicationCleanup(pdfApp);
}
